import React, { useMemo, Fragment, useRef, useEffect } from 'react';
import T from 'prop-types';
import { get, map, keys, mapValues, max, range, reduce, isEmpty, min, values } from 'lodash-es';

import Skeleton from '../../../../components/Skeleton';
import { SmartChart } from '../../../../components/Chart';
import Button from '../../../../components/Buttons/Button';
import { chartQuarterConfig, formatToDatePeriod } from '../config/finance/charts';
import { useTableScrollState } from '../../../../components/StickyTableLayout/hooks';

import ChartTable from './ChartTable';

const colors = {
  prev_period: '#BEC9DB',
  curr_period: '#15CFD4',
};

const dashStyle = {
  prev_period: 'Dot',
  curr_period: 'Solid',
};

const emptySeries = {
  prev_period: [],
  curr_period: [],
};

const QuarterChart = ({
  loading,
  series,
  period = 'daily',
  metric,
  toDatePeriod,
  height = 232,
  skeletonHeight = 232,
  animation,
  hasTable = false,
  image = false,
  showTable,
  showWatermark = false,
  tableScrollLeft,
  variant,
  toggleSize = 'sm',
  footer,
  onShowTable,
  onScrollTable,
}) => {
  const seriesByMetric = get(series, [period, metric], emptySeries);

  const maxLength = max(map(seriesByMetric, value => value.length));
  const labelStep = Math.floor(maxLength / 10);
  const categories = range(1, maxLength + 1);

  const chartConfig = useMemo(() => {
    const minPoints = mapValues(seriesByMetric, data => min(data.map(item => item.value)));
    const maxPoints = mapValues(seriesByMetric, data => max(data.map(item => item.value)));
    const minPoint = min(values(minPoints));
    const maxPoint = max(values(maxPoints));
    const deviation = (maxPoint - minPoint) * 0.1;

    return chartQuarterConfig({
      metric,
      toDatePeriod,
      labelStep,
      height,
      animation,
      floor: Math.floor(minPoint - deviation),
      ceiling: Math.ceil(maxPoint + deviation),
      variant,
    });
  }, [metric, toDatePeriod, labelStep, height, animation, seriesByMetric, variant]);

  const seriesData = useMemo(() => {
    if (isEmpty(seriesByMetric.curr_period)) { return []; }

    return (
      reduce(seriesByMetric, (acc, value, key) => ({
        ...acc,
        [key]: value.map((elem, index) => ({
          ...elem,
          y: elem.value,
          x: index,
          color: colors[key],
          periodLabel: formatToDatePeriod(elem.date, toDatePeriod),
        })),
      }), {})
    );
  }, [seriesByMetric, toDatePeriod]);

  const metaSeriesData = useMemo(() => (
    mapValues(seriesData, (_, key) => ({
      color: colors[key],
      dataLabels: {
        crop: false,
        enabled: false,
        overflow: 'allow',
        padding: 3,
        useHTML: true,
      },
      dashStyle: dashStyle[key],
      marker: {
        enabled: true,
        symbol: 'circle',
        radius: 4,
      },
      name: key,
      threshold: 0,
      type: 'spline',
      visible: true,
    }))
  ), [seriesData]);

  const legends = keys(seriesData).map(legend => ({
    label: seriesData[legend][0]?.periodLabel,
    color: colors[legend],
  }));

  const tableLayoutRef = useRef();
  const tableWrapperRef = useRef();

  const scrollState = hasTable
    ? useTableScrollState(tableLayoutRef, {
      scrollableSelector: '.app-finance-table-wrapper',
    })
    : {};

  useEffect(() => {
    if (onScrollTable) onScrollTable(scrollState.scrollLeft);
  }, [scrollState.scrollLeft]);

  const localScroll = () => {
    const presentRef = tableLayoutRef.current;
    const tableCurrentCell = presentRef && presentRef.querySelector('.js-scroll-selector');
    const nextLeft = tableCurrentCell && (tableCurrentCell.offsetLeft - tableLayoutRef.current.clientWidth + 10);
    if (onScrollTable) onScrollTable(nextLeft);

    if (tableWrapperRef.current) {
      tableWrapperRef.current.scrollLeft = image ? tableScrollLeft : nextLeft;
    }
  };

  useEffect(() => {
    if (!isEmpty(series)) localScroll();
  }, [series]);

  return (
    <div className="quarter-chart-wrapper">
      {loading
        ? <Skeleton className="year-chart-wrapper-box" height={skeletonHeight} />
        : (
          <>
            <div className="quarter-chart-wrapper-box">
              <SmartChart
                chartId="quarter-chart"
                series={seriesData}
                metaSeriesData={metaSeriesData}
                categories={categories}
                config={chartConfig}
              />
            </div>
            <div className="quarter-chart-legend">
              {legends.reverse().map(({ label, color }) => (
                <div key={label} className="quarter-chart-legend-item">
                  <span style={{ backgroundColor: color }} /> {label}
                </div>
              ))}
            </div>
          </>
        )}
      {hasTable && (
        <Fragment>
          <div className="table-toggle-wrapper">
            {!loading && (
              <Button
                size={toggleSize || undefined}
                shape="square"
                variant="secondary"
                className="table-toggle-button"
                active={showTable}
                onClick={onShowTable}
              >
                <i className="hicon-list-button" />
              </Button>
            )}
          </div>
          <ChartTable
            show={showTable}
            loading={loading}
            variant="quarter"
            tableData={seriesData}
            tableLayoutRef={tableLayoutRef}
            tableWrapperRef={tableWrapperRef}
            scrollState={scrollState}
          />
        </Fragment>
      )}
      {(showWatermark || footer) && (
        <div className="quarter-chart-footer">
          <div className="highcharts-watermark" />
          {footer}
        </div>
      )}
    </div>
  );
};

QuarterChart.propTypes = {
  series: T.object,
  period: T.string,
  metric: T.string,
  toDatePeriod: T.string,
  height: T.number,
  skeletonHeight: T.number,
  tableScrollLeft: T.number,
  loading: T.bool,
  animation: T.bool,
  variant: T.oneOf(['qv', 'v1', 'v2']),
  hasTable: T.bool,
  image: T.bool,
  showTable: T.bool,
  showWatermark: T.bool,
  toggleSize: T.string,
  footer: T.node,
  onShowTable: T.func,
  onScrollTable: T.func,
};

export default QuarterChart;
